const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })

const weekdayFormatter = new Intl.DateTimeFormat(undefined, { weekday: 'long' })
const dayMonthFormatter = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long' })
const dayMonthYearFormatter = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})
const shortDateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})
const fullDateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})
const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function calendarDayDiff(a: Date, b: Date): number {
  return Math.round(
    (startOfLocalDay(a).getTime() - startOfLocalDay(b).getTime()) / 86_400_000,
  )
}

function dateFromEpochSeconds(epochSeconds: number): Date {
  return new Date(epochSeconds * 1000)
}

export function dayKey(epochSeconds: number): string {
  const d = dateFromEpochSeconds(epochSeconds)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function dayLabel(epochSeconds: number): string {
  const d = dateFromEpochSeconds(epochSeconds)
  const now = new Date()
  const daysAgo = calendarDayDiff(now, d)
  if (daysAgo === 0) return 'Today'
  if (daysAgo === 1) return 'Yesterday'
  if (daysAgo > 0 && daysAgo < 7) return weekdayFormatter.format(d)
  if (d.getFullYear() === now.getFullYear()) return dayMonthFormatter.format(d)
  return dayMonthYearFormatter.format(d)
}

export function timeLabel(epochSeconds: number): string {
  return timeFormatter.format(dateFromEpochSeconds(epochSeconds))
}

export function fullDateTimeLabel(epochSeconds: number): string {
  return fullDateTimeFormatter.format(dateFromEpochSeconds(epochSeconds))
}

export function shortDateTimeLabel(epochSeconds: number): string {
  return shortDateTimeFormatter.format(dateFromEpochSeconds(epochSeconds))
}

export function relativeUptime(createdAt?: number | null): string | null {
  if (!createdAt) return null
  const ageS = Math.floor(Date.now() / 1000) - createdAt
  if (ageS < 60) return `${ageS}s`
  if (ageS < 3600) return `${Math.floor(ageS / 60)}m`
  if (ageS < 86_400) return `${Math.floor(ageS / 3600)}h`
  const days = Math.floor(ageS / 86_400)
  return days < 30 ? `${days}d` : `${Math.floor(days / 30)}mo`
}

/** WhatsApp-style "last seen" / "last replied" relative phrase. */
export function relativeLastReply(ts?: number | null): string | null {
  if (!ts) return null
  const ageS = Math.floor(Date.now() / 1000) - ts
  if (ageS < 30) return "just now"
  if (ageS < 60) return `${ageS}s ago`
  if (ageS < 3600) {
    const m = Math.floor(ageS / 60)
    return m === 1 ? "1m ago" : `${m}m ago`
  }
  if (ageS < 86_400) {
    const h = Math.floor(ageS / 3600)
    return h === 1 ? "1h ago" : `${h}h ago`
  }
  const d = Math.floor(ageS / 86_400)
  return d === 1 ? "yesterday" : `${d}d ago`
}

export function relativeTimeFromNow(epochSeconds: number): string {
  const diffSeconds = Math.round(epochSeconds - Date.now() / 1000)
  const abs = Math.abs(diffSeconds)
  if (abs < 45) return rtf.format(diffSeconds, 'second')
  const minutes = Math.round(diffSeconds / 60)
  if (Math.abs(minutes) < 45) return rtf.format(minutes, 'minute')
  const hours = Math.round(diffSeconds / 3600)
  if (Math.abs(hours) < 22) return rtf.format(hours, 'hour')
  const days = Math.round(diffSeconds / 86_400)
  if (Math.abs(days) < 26) return rtf.format(days, 'day')
  const months = Math.round(diffSeconds / 2_629_746)
  if (Math.abs(months) < 12) return rtf.format(months, 'month')
  const years = Math.round(diffSeconds / 31_556_952)
  return rtf.format(years, 'year')
}
